 import { Link } from "react-router-dom";
 import { ArrowRight, Package, Recycle, DollarSign, Wrench, ClipboardCheck } from "lucide-react";
 import { motion } from "framer-motion";
 
 const services = [
   {
     icon: Package,
     title: "New Racking Systems",
     description: "Brand new pallet racking from top manufacturers. All systems meet industry safety standards with full warranties.",
     href: "/services/new",
     accent: true,
   },
   {
     icon: Recycle,
     title: "Used Racking Systems",
     description: "Quality-inspected used racking at 40-60% savings. Every system passes our rigorous 50-point inspection.",
     href: "/services/used",
     accent: false,
   },
   {
     icon: DollarSign,
     title: "Sell Your Racking",
     description: "Get top dollar for your surplus inventory. We handle pickup, logistics, and provide fast payment.",
     href: "/services/sell",
     accent: false,
   },
   {
     icon: Wrench,
     title: "Installation Services",
     description: "Professional installation by certified crews. Turnkey solutions from delivery to final walkthrough.",
     href: "/services/installation",
     accent: false,
   },
   {
     icon: ClipboardCheck,
     title: "Inspection & Safety",
     description: "Comprehensive rack inspections per RMI and OSHA guidelines. Protect your team and investment.",
     href: "/services/inspection",
     accent: false,
   },
 ];
 
 const containerVariants = {
   hidden: { opacity: 0 },
   visible: {
     opacity: 1,
     transition: {
       staggerChildren: 0.1,
     },
   },
 };
 
 const itemVariants = {
   hidden: { opacity: 0, y: 20 },
   visible: { opacity: 1, y: 0 },
 };
 
 export function Services() {
   return (
     <section className="py-16 md:py-24 bg-background">
       <div className="container">
         {/* Section header */}
         <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
           <span className="inline-block px-4 py-1.5 rounded-full bg-accent/10 text-accent text-sm font-semibold mb-4">
             Our Services
           </span>
           <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
             Complete Warehouse Racking Solutions
           </h2>
           <p className="text-lg text-muted-foreground">
             From sourcing to installation, we're your single point of contact for all pallet racking needs.
           </p>
         </div>
 
         {/* Services grid */}
         <motion.div 
           className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
           variants={containerVariants}
           initial="hidden"
           whileInView="visible"
           viewport={{ once: true, margin: "-100px" }}
         >
           {services.map((service, index) => (
             <motion.div key={service.title} variants={itemVariants}>
               <Link
                 to={service.href}
                 className={`group block h-full p-6 md:p-8 rounded-2xl border transition-all duration-300 hover:-translate-y-1 ${
                   service.accent 
                     ? "bg-accent text-accent-foreground border-accent shadow-lg hover:shadow-xl" 
                     : "bg-card border-border shadow-card hover:shadow-elevated hover:border-accent/50"
                 }`}
               >
                 <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4 ${
                   service.accent 
                     ? "bg-accent-foreground/20" 
                     : "bg-accent/10"
                 }`}>
                   <service.icon className={`h-6 w-6 ${service.accent ? "text-accent-foreground" : "text-accent"}`} />
                 </div>
                 
                 <h3 className={`text-xl font-bold mb-2 ${
                   service.accent ? "text-accent-foreground" : "text-foreground"
                 }`}>
                   {service.title}
                 </h3>
                 
                 <p className={`mb-4 ${
                   service.accent ? "text-accent-foreground/80" : "text-muted-foreground"
                 }`}>
                   {service.description}
                 </p>
                 
                 <span className={`inline-flex items-center gap-1.5 text-sm font-semibold group-hover:gap-2.5 transition-all ${
                   service.accent ? "text-accent-foreground" : "text-accent"
                 }`}>
                   Learn More
                   <ArrowRight className="h-4 w-4" />
                 </span>
               </Link>
             </motion.div>
           ))}
         </motion.div>
       </div>
     </section>
   );
 }